import {useCallback, useEffect, useState} from "react";
import {localDocumentsService} from "../services/local-documents.service";
import {
    LocalDocumentCategory,
    LocalDocumentContentResponse,
    LocalDocumentHistoryResponse,
    LocalDocumentNavigationData,
    LocalDocumentOcrVersionResponse,
    OcrVersionPagesWithText
} from "../models/local-documents.models";
import {useNavigate, useSearchParams} from "react-router-dom";
import {RouteHelpers} from "../config/routeConstants";
import {useSnackbar} from "../contexts/SnackbarContext";
import {useTranslation} from "react-i18next";

export const useDigitizationDetails = (documentId: number | undefined) => {
    const [searchParams] = useSearchParams();
    const params = Object.fromEntries(searchParams.entries());

    const navigate = useNavigate();
    const {t} = useTranslation();

    const [contentData, setContentData] = useState<LocalDocumentContentResponse | null>(null);
    const [navigationData, setNavigationData] = useState<LocalDocumentNavigationData | null>(null);

    const [historyData, setHistoryData] = useState<LocalDocumentHistoryResponse[]>([]);

    const [originalBaseContent, setOriginalBaseContent] = useState<Uint8Array  | null>(null);
    const [ocrResult, setOcrResult] = useState<LocalDocumentOcrVersionResponse | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const {showSnackbar} = useSnackbar();

    const fetchData = useCallback(async () => {
        if (!documentId) return;

        setIsLoading(true);
        setError(null);

        try {
            const [navigationContent, original, ocrVersion, history] = await Promise.all([
                localDocumentsService.fetchNavigationPages({id: documentId, params}),
                localDocumentsService.fetchOriginalVersion(documentId),
                localDocumentsService.fetchOcrVersion(documentId),
                localDocumentsService.fetchHistory(documentId),
            ]);

            const { document, ...navigation } = navigationContent;

            setContentData(document);
            setNavigationData(navigation);
            setHistoryData(history);
            setOriginalBaseContent(original);
            setOcrResult(ocrVersion);
        } catch (err) {
            setError(err as Error);
            console.error(t('digitizationDetails.messages.fetchDataError'), err);
            showSnackbar(t('digitizationDetails.messages.fetchDataError'), "error");
        } finally {
            setIsLoading(false);
        }
    }, [documentId, t]);

    useEffect(() => {
        void fetchData()
    }, [documentId, t]);

    const navigateToPreviousDocument = () => {
        if (!navigationData || !navigationData?.hasPrevious) return;

        const {previousDocumentId} = navigationData;
        navigate(RouteHelpers.digitizationDetails(previousDocumentId, searchParams));
    };

    const navigateToNextDocument = () => {
        if (!navigationData || !navigationData?.hasNext) return;

        const {nextDocumentId} = navigationData;
        navigate(RouteHelpers.digitizationDetails(nextDocumentId, searchParams));
    };

    const navigateBackToList = () => {
        navigate(RouteHelpers.documentsDigitization(searchParams));
    };

    const navigateToEdition = () => {
        if (!documentId) return;
        navigate(RouteHelpers.digitizationEdition(documentId));
    };

    const navigateBackToDetails = () => {
        if (!documentId) return;
        navigate(RouteHelpers.digitizationDetails(documentId));
    };

    const cancelDocument = () => {
        // console.log("Cancel this document");
    };

    // TODO: verify when it should be called
    const confirmDocument = async () => {
        if (!documentId) return;

        try {
            await localDocumentsService.approveDocument(documentId);
            await fetchData();

            showSnackbar(t('digitizationDetails.messages.approveDocumentSuccess'), "success");
        } catch (err) {
            setError(err as Error);
            console.error(t('digitizationDetails.messages.approveDocumentError'), err);
            showSnackbar(t('digitizationDetails.messages.approveDocumentError'), "error");
        }
    };

    const confirmOcrVersion = async (body: OcrVersionPagesWithText) => {
        if (!documentId) return;

        try {
            await localDocumentsService.approveOcrResultChanges(documentId, body);
            showSnackbar(t('digitizationDetails.messages.approveOcrResultChangesSuccess'), "success");
        } catch (err) {
            setError(err as Error);
            console.error(t('digitizationDetails.messages.approveOcrResultChangesError'), err);
            showSnackbar(t('digitizationDetails.messages.approveOcrResultChangesError'), "error");
        }
    };

    const changeDocumentCategory = async (category: LocalDocumentCategory) => {
        if (!documentId) return;

        try {
            await localDocumentsService.updateDocumentCategory(documentId, category);

            await fetchData();
            showSnackbar(t('digitizationDetails.messages.updateDocumentCategorySuccess'), 'success');
        } catch (err) {
            setError(err as Error);
            console.error(t('digitizationDetails.messages.updateDocumentCategoryError'), err);
            showSnackbar(t('digitizationDetails.messages.updateDocumentCategoryError'), 'error');
        }
    };

    const exportDocument = async () => {
        if (!documentId) return;

        try {
            const response = await localDocumentsService.exportLocalDocumentsToCSV([documentId]);
            return true;
        } catch (error) {
            console.error('Error exporting file:', error);
            return false;
        }
    };

    return {
        contentData,
        navigationData,
        historyData,
        originalBaseContent,
        ocrResult,
        isLoading,
        error,
        navigateToPreviousDocument,
        navigateToNextDocument,
        navigateBackToList,
        navigateToEdition,
        navigateBackToDetails,
        cancelDocument,
        confirmDocument,
        confirmOcrVersion,
        changeDocumentCategory,
        exportDocument,
    };
};